<template>
    <div :class="compact ? '' : 'mb-4 px-2'">
        <p v-if="!compact" class="font-medium text-xs text-slate-700 mb-1">
            Organization Switcher
            <UBadge v-if="isLoadingOrgs" color="neutral" variant="subtle" size="sm" class="ml-1">Loading...</UBadge>
        </p>
        <UPopover :content="{ side: 'bottom', align: 'start' }" v-model:open="popoverOpen">
            <UButton
                :label="currentOrgLabel"
                trailing-icon="i-ion-chevron-down"
                size="sm"
                color="neutral" variant="outline"
                class="w-full max-w-full justify-between truncate"
                :loading="isLoadingOrgs"
                :disabled="isLoadingOrgs"
            />
            <template #content>
                <div class="p-2 min-w-64 max-w-80 max-h-96 overflow-hidden flex flex-col">
                    <!-- Search bar -->
                    <UInput
                        ref="searchInputRef"
                        v-model="searchQuery"
                        placeholder="Search organizations..."
                        icon="i-ion-search"
                        size="sm"
                        class="mb-2"
                        :loading="isSearching"
                        @keydown.enter="selectFirstFiltered"
                    />

                    <!-- Organization list -->
                    <div class="overflow-auto flex-1 space-y-0.5 mb-2">
                        <button
                            v-for="org in displayedOrganizations"
                            :key="org.id"
                            class="w-full text-left px-2 py-1.5 rounded text-sm hover:bg-slate-100 transition-colors truncate"
                            :class="{ 'bg-slate-100 font-medium': org.id === selectedOrgId }"
                            @click="handleOrgChange(org.id)"
                        >
                            {{ org.active ? org.name : `${org.name} (inactive)` }}
                        </button>
                        <p v-if="displayedOrganizations.length === 0" class="text-xs text-slate-500 px-2 py-1.5">
                            No organizations found
                        </p>
                    </div>

                    <!-- Navigation buttons -->
                    <div class="grid grid-cols-2 gap-2 border-t pt-2">
                        <UButton
                            label="First Active"
                            size="sm"
                            color="neutral"
                            variant="outline"
                            block
                            :disabled="isFirstActiveOrgDisabled"
                            @click="goToFirstActiveOrg"
                        />
                        <UTooltip :text="isLastActiveOrg ? 'Already at last active org' : undefined" class="block">
                            <UButton
                                label="Next Active"
                                size="sm"
                                color="neutral"
                                variant="outline"
                                block
                                :disabled="isLastActiveOrg"
                                @click="goToNextActiveOrg"
                            />
                        </UTooltip>
                    </div>
                </div>
            </template>
        </UPopover>
        <p v-if="error" class="text-xs text-red-500 mt-1">{{ error }}</p>
    </div>
</template>

<script setup lang="ts">
defineProps<{
    compact?: boolean;
}>();

// Get organization switcher functionality
const {
    organizations,
    switchOrganization,
    resetOrganization: resetToOriginalOrg,
    error: orgSwitcherError,
    isLoading: isLoadingOrgs
} = useOrganizationSwitcher();

// Get current session data for selected org
const [sessionData] = useSessionData();
const selectedOrgId = ref<number | undefined>(undefined);
const searchQuery = ref('');
const searchInputRef = ref<{ input: HTMLInputElement } | null>(null);
const popoverOpen = ref(false);
const searchResults = ref<Array<{ id: number; name: string; active: boolean }> | null>(null);
const isSearching = ref(false);
let searchTimeout: ReturnType<typeof setTimeout> | null = null;

watch(popoverOpen, (open) => {
    if (open) {
        nextTick(() => {
            searchInputRef.value?.input?.focus();
        });
    } else {
        // Reset search when popover closes
        searchQuery.value = '';
        searchResults.value = null;
    }
});

// Set initial selected org when data is available
watch(sessionData, (data) => {
    if (!data || !data.globalAdmin) return;
    selectedOrgId.value = data.orgId;
}, { immediate: true });

// Debounced backend search
watch(searchQuery, (query) => {
    if (searchTimeout) clearTimeout(searchTimeout);

    const trimmed = query.trim();
    if (!trimmed) {
        searchResults.value = null;
        isSearching.value = false;
        return;
    }

    isSearching.value = true;
    searchTimeout = setTimeout(async () => {
        try {
            searchResults.value = await $fetch('/api/admin/organizations/switcher', {
                query: { search: trimmed },
            });
        } catch {
            searchResults.value = null;
        } finally {
            isSearching.value = false;
        }
    }, 300);
});

// Show search results when searching, otherwise show default active orgs
const displayedOrganizations = computed(() => {
    if (searchQuery.value.trim()) {
        return searchResults.value || [];
    }
    return organizations.value || [];
});

// Current organization label
const currentOrgLabel = computed(() => {
    if (!selectedOrgId.value) return 'Select Organization';
    const currentOrg = organizations.value?.find(org => org.id === selectedOrgId.value);
    return currentOrg ? currentOrg.name : `Organization #${selectedOrgId.value}`;
});

// Check if current org is the first active org
const isFirstActiveOrgDisabled = computed(() => {
    if (!organizations.value?.length) return true;
    return selectedOrgId.value === organizations.value[0].id;
});

// Check if current org is the last active org
const isLastActiveOrg = computed(() => {
    if (!organizations.value?.length) return true;
    const currentIndex = organizations.value.findIndex(org => org.id === selectedOrgId.value);
    return currentIndex === organizations.value.length - 1 || currentIndex === -1;
});

// Handle organization change
async function handleOrgChange(orgId: number) {
    if (orgId === sessionData.value?.orgId) return;
    await switchOrganization(orgId);
    window.location.href = '/';
}

// Go to first active org
async function goToFirstActiveOrg() {
    if (!organizations.value?.length) return;
    const firstActiveOrg = organizations.value[0];
    await handleOrgChange(firstActiveOrg.id);
}

// Go to next active org
async function goToNextActiveOrg() {
    if (!organizations.value?.length) return;
    const currentIndex = organizations.value.findIndex(org => org.id === selectedOrgId.value);
    if (currentIndex === -1 || currentIndex === organizations.value.length - 1) return;
    const nextActiveOrg = organizations.value[currentIndex + 1];
    await handleOrgChange(nextActiveOrg.id);
}

// Select the first filtered organization on Enter
function selectFirstFiltered() {
    if (displayedOrganizations.value.length > 0) {
        handleOrgChange(displayedOrganizations.value[0].id);
    }
}

// Computed property for the error message
const error = computed(() => orgSwitcherError.value);
</script>
